import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownLeft, ArrowRight, Sparkles, ShieldAlert, History, CreditCard } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { useTheme } from "../contexts/ThemeContext";
import DigitalCardHero from "../components/DigitalCardHero";
import HomeDiscovery from "../components/HomeDiscovery";
import SponsoredCard from "../components/SponsoredCard";
import SpinWinnersBanner from "../components/SpinWinnersBanner";
import AdsRotator from "../components/AdsRotator";
import SupportWidget from "../components/SupportWidget";
import WhatsAppChannelCard from "../components/WhatsAppChannelCard";
import ActiveServices from "../components/ActiveServices";
import ShopNetwork from "../components/ShopNetwork";
import BannerCarousel from "../components/BannerCarousel";
import CampaignsStrip from "../components/CampaignsStrip";
import ReorderableGrid from "../components/ReorderableGrid";
import { quickAccessForRole, recordTileOpen, sortTilesByUsage, favoriteTileTo } from "../lib/serviceTiles";
import { guardianSwitchBack as guardianSwitchBackHelper } from "../lib/guardianSwitch";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";
import LogoGate from "../components/LogoGate";

/**
 * Round 52 · "Dashboard IA Redesign" (Feb 28 2026).
 *
 * Home is no longer the iCaard service catalogue. It now answers ONE question:
 * "What needs my attention right now?".
 *
 *   1. Festive greeting (Project C theme).
 *   2. Digital Card.
 *   3. Wallet balance + quick top-up.
 *   4. Active Services — every in-progress item across the platform.
 *   5. Quick Access — 6 most-used services for the user's role.
 *   6. Recent Activity — last 5 wallet transactions.
 *
 * Every service is still reachable from /app/all-services (the new directory).
 */
// Round 109/110 — Home page is LOCKED until the user uploads BOTH a profile
// image AND an ID document (students & admin exempt from ID).
function ProfilePhotoGate() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  // Guardians managing a child are never trapped by the CHILD's missing uploads.
  if (!user || user.role === 'customer' || localStorage.getItem("icard_token_parent")) return null;
  const needsPhoto = !user.profile_image_url && !user.selfie_url;
  const needsId = !user.ghana_card_url && !["student", "admin"].includes(user.role);
  if (!needsPhoto && !needsId) return null;
  const onPhotoUploaded = async (url) => {
    if (!url) return;
    setBusy(true);
    try {
      await api.post("/auth/profile/photo", { url });
      await refresh();
      toast.success("Profile photo saved ✅", { duration: 6000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the photo — try again."); }
    setBusy(false);
  };
  const onIdUploaded = async (url) => {
    if (!url) return;
    setBusy(true);
    try {
      await api.post("/auth/id-document", { url });
      await refresh();
      toast.success("ID document saved. your Home Page is unlocked ✅", { duration: 7000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the ID — try again."); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[85] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="profile-photo-gate">
      <div className="bg-[#13131E] border border-[#F5B400]/50 rounded-2xl p-5 w-full max-w-sm text-center space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="text-4xl">🔒</div>
        <div className="text-base font-black text-[#F5B400]" data-testid="photo-gate-warning">Your Home Page is temporarily locked</div>
        {needsPhoto ? (
          <>
            <p className="text-xs text-[#C7D2FE]">Upload your <b>profile image file</b> to continue. {needsId ? "Your ID document will be requested next." : "It unlocks automatically right after the upload."}</p>
            <ImageUploader kind="selfie" label="Profile image *" onUploaded={onPhotoUploaded} testId="photo-gate-upload" />
          </>
        ) : (
          <>
            <p className="text-xs text-[#C7D2FE]" data-testid="id-gate-warning">Upload your <b>ID document file</b> (Ghana Card, passport…. image or PDF) to unlock your Home Page. It unlocks automatically right after the upload.</p>
            <ImageUploader kind="ghana_card" label="ID document *" allowPdf onUploaded={onIdUploaded} testId="id-gate-upload" />
          </>
        )}
        {busy && <div className="text-[10px] font-bold text-[#9CA3D4]">Saving…</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const theme = useTheme();
  const nav = useNavigate();

  const quickTiles = sortTilesByUsage(quickAccessForRole(user).filter((t) => isEnabled(t.flag)));
  const favouriteTo = favoriteTileTo();
  const [card, setCard] = useState(null);
  const [txns, setTxns] = useState([]);
  const [photoOpen, setPhotoOpen] = useState(false);
  useEffect(() => {
    if (!photoOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = e => { if (e.key === "Escape") setPhotoOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
      document.querySelector('[data-testid="profile-photo-preview-open"]')?.focus();
    };
  }, [photoOpen]);
  const [schoolLink, setSchoolLink] = useState(null); // {to, label} — Round 89
  useEffect(() => {
    api.get("/school/my").then((r) => {
      if (r.data.enrolled) setSchoolLink({ to: "/app/my-school", label: "MY SCHOOL", desc: "Activities, attendance & payments" });
      else if (r.data.is_staff) setSchoolLink({ to: "/app/school", label: "School Console", desc: r.data.staff_role === "owner" ? "Manage your school" : "Attendance, enrollment & fees" });
      else if (user?.role === "school_admin") setSchoolLink({ to: "/app/school", label: "Set Up Your School", desc: "Create your school to start enrolling students" });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/cards/me").then((r) => setCard(r.data)).catch(() => {});
    api.get("/wallet/transactions?limit=5").then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  const guardianParent = localStorage.getItem("icard_token_parent");
  const guardianSwitchBack = () => guardianSwitchBackHelper();
  // Logo gate only fires once the photo/ID gate has been satisfied.
  const photoGateActive = user && user.role !== 'customer' && !guardianParent &&
    ((!user.profile_image_url && !user.selfie_url) || (!user.ghana_card_url && !["student", "admin"].includes(user.role)));
  const firstName = (user?.name || "").split(" ").slice(0, 1).join(" ") || "friend";
  const showGreeting = theme.id !== "default" && theme.greeting;
  const hour = new Date().getHours();
  const dayGreeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";

  const [greetingTop,setGreetingTop]=useState(80);
  useEffect(()=>{const header=document.querySelector('.icaard-topbar');if(!header)return;const update=()=>setGreetingTop(header.getBoundingClientRect().height);update();const observer=new ResizeObserver(update);observer.observe(header);return()=>observer.disconnect();},[]);
  return (
    <div className="space-y-4 sm:space-y-6 stagger" data-testid="dashboard-page">
      <ProfilePhotoGate />
      <LogoGate active={!photoGateActive && !guardianParent} />
      {photoOpen && createPortal(
        <div role="dialog" aria-modal="true" aria-label="Profile photo preview" data-testid="profile-photo-preview"
             style={{position:"fixed",inset:0,zIndex:100,background:"rgba(18,44,56,.78)",backdropFilter:"blur(10px)"}}>
          <button autoFocus aria-label="Close profile photo preview" onClick={() => setPhotoOpen(false)}
                  style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,padding:24}}>
            <img src={user?.profile_image_url || user?.selfie_url} alt={user?.name}
                 style={{width:"min(78vw,360px)",height:"min(78vw,360px,65dvh)",objectFit:"contain",background:"#F4F7F5",borderRadius:32,border:"6px solid white",boxShadow:"0 20px 70px #0005"}} />
            <span style={{color:"white",fontSize:18,fontWeight:700}}>{user?.name}</span>
            <span style={{color:"#D4E6E2",fontSize:12}}>Tap anywhere to close</span>
          </button>
        </div>,document.body)}
      {/* Greeting + avatar — user's own info ALWAYS above any promotion */}
      <header className="icaard-home-greeting flex items-center justify-between gap-3" style={{top:greetingTop}}>
        <div className="min-w-0">
          {showGreeting ? (
            <>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold flex items-center gap-1.5" style={{ color: theme.palette }}>
                <Sparkles size={11} /> {theme.is_birthday ? "It's your day" : "Today on iCaard"}
              </div>
              <h1 data-testid="dashboard-festive-greeting" className="font-display font-black text-xl sm:text-2xl md:text-3xl truncate" style={{ color: theme.palette }}>{theme.greeting}</h1>
            </>
          ) : (
            <>
              <div className="text-[#B4B4C7] text-xs sm:text-sm">{dayGreeting},</div>
              <h1 className="font-display font-black text-xl sm:text-2xl md:text-3xl truncate">{firstName} 👋</h1>
              <div className="text-sm text-[#6F7285] mt-1">Your everyday services, all together.</div>
            </>
          )}
        </div>
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
          {(user?.profile_image_url || user?.selfie_url) ? (
            <button type="button" aria-label="View profile photo" data-testid="profile-photo-preview-open" onClick={() => setPhotoOpen(true)} className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
            <img src={user.profile_image_url || user.selfie_url} alt={user.name} className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border-2"
                 style={{ borderColor: guardianParent ? "#EC4899" : theme.palette || "#7C3AED" }} />
            </button>
          ) : (
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#13131E] border-2 flex items-center justify-center text-base sm:text-lg font-display font-black"
                 style={{ borderColor: guardianParent ? "#EC4899" : theme.palette || "#7C3AED", color: theme.palette || "#A78BFA" }}>
              {user?.name?.[0]}
            </div>
          )}
          {guardianParent && (
            <button onClick={guardianSwitchBack} data-testid="dashboard-guardian-switch-back"
                    className="px-2.5 py-1 rounded-full bg-[#EC4899] text-white text-[9px] font-black shadow-lg whitespace-nowrap hover:scale-105 transition-transform">
              ⬅ Switch back
            </button>
          )}
        </div>
      </header>

      <HomeDiscovery>
          {/* 1 · Premium Digital iCaard */}
          <DigitalCardHero user={user} card={card} />

          {/* Card management — report missing / block, usage history, full card */}
          <div className="grid grid-cols-3 gap-2" data-testid="card-actions-row">
            <button data-testid="card-action-report" onClick={() => nav("/app/card")}
                    className="surface rounded-xl px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-[#1A1A27] transition-colors">
              <ShieldAlert size={16} className="text-[#F59E0B]" />
              <span className="text-[10px] font-bold text-white">Report / Block</span>
            </button>
            <button data-testid="card-action-history" onClick={() => nav("/app/card/history")}
                    className="surface rounded-xl px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-[#1A1A27] transition-colors">
              <History size={16} className="text-[#A78BFA]" />
              <span className="text-[10px] font-bold text-white">Usage history</span>
            </button>
            <button data-testid="card-action-full" onClick={() => nav("/app/card")}
                    className="surface rounded-xl px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-[#1A1A27] transition-colors">
              <CreditCard size={16} className="text-[#10B981]" />
              <span className="text-[10px] font-bold text-white">Manage card</span>
            </button>
          </div>

      </HomeDiscovery>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* LEFT — card + active services + quick access */}
        <div className="lg:col-span-2 min-w-0 space-y-4 sm:space-y-5">
          {/* Global Spin-to-Win winner banner */}
          {user?.role === "customer" && isEnabled("service.spin") && <SpinWinnersBanner />}

          {/* Sponsored ad — below the digital card and before service discovery */}
          <SponsoredCard />

          {/* Discover — admin banners live BELOW the user's own info */}
          <BannerCarousel />

          {isEnabled("service.directory") && <ShopNetwork />}

          {/* School console link — lives with the service shortcuts (Round 103) */}
          {schoolLink && isEnabled("service.school") && (
            <a href={schoolLink.to} data-testid="my-school-tile"
               className="block rounded-2xl border border-[#16A34A]/50 bg-[#16A34A]/10 px-4 py-3">
              <div className="text-xs font-black text-[#16A34A]">🎓 {schoolLink.label}</div>
              <div className="text-[11px] text-[#9CA3D4] mt-0.5">{schoolLink.desc}</div>
            </a>
          )}

          {/* Quick Access — 6 most-used tiles only */}
          {user?.role !== "customer" && quickTiles.length > 0 && (
            <section data-testid="dashboard-quick-access">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#A78BFA]">Quick Access</h2>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline text-[9px] text-[#6F7285]">Press and hold a tile to rearrange</span>
                  <button onClick={() => nav("/app/all-services")}
                        data-testid="dashboard-all-services-link"
                        className="text-xs font-semibold text-[#A78BFA] flex items-center gap-1 hover:text-white">
                  All Services <ArrowRight size={11} />
                  </button>
                </div>
              </div>
              <ReorderableGrid
                tiles={quickTiles}
                orderKey="quick"
                pinnedTo={favouriteTo}
                className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3"
                renderTile={(t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      data-testid={t.testid || `tile-${t.label.toLowerCase().replace(/ /g, "-")}`}
                      onClick={() => { recordTileOpen(t.to); nav(t.to); }}
                      data-card-tone={t.color} className="icaard-service-card surface w-full rounded-2xl p-3 sm:p-4 text-left hover:bg-[#1A1A27] transition-colors min-h-[100px] sm:min-h-[110px] flex flex-col gap-2"
                    >
                      {t.to === favouriteTo && <span className="icaard-quick-favourite">★ Favourite</span>}
                      <div className={`icaard-service-icon w-12 h-12 rounded-xl ${t.color} flex items-center justify-center text-white`}>
                        <Icon size={22} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-white text-xs sm:text-sm leading-tight">{t.label}</div>
                        <div className="text-[10px] text-[#9CA3D4] mt-0.5 line-clamp-2">{t.desc}</div>
                      </div>
                    </button>
                  );
                }}
              />
            </section>
          )}
        </div>

        {/* RIGHT — active services + recent activity moved DOWN (Round 100 swap) */}
        <div className="lg:col-span-1 space-y-5">
          <ActiveServices />
          {/* Recent transactions — hidden entirely when empty */}
          {txns.length > 0 && isEnabled("service.wallet") && (
          <section data-testid="dashboard-recent-activity">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#A78BFA]">Recent Activity</h2>
              <button data-testid="view-all-txns" onClick={() => nav("/app/transactions")}
                      className="text-xs font-semibold text-[#A78BFA] flex items-center gap-1 hover:text-white">
                See All <ArrowRight size={11} />
              </button>
            </div>
            <div className="surface rounded-2xl divide-y divide-[#26263A]">
              {txns.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.amount < 0 ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
                    {t.amount < 0 ? <ArrowUpRight size={15} /> : <ArrowDownLeft size={15} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate text-white">{t.description}</div>
                    <div className="text-[10px] text-[#9CA3D4]">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`text-sm font-bold font-mono-num ${t.amount < 0 ? "text-white" : "text-[#10B981]"}`}>
                    {t.amount < 0 ? "-" : "+"}{fmt(Math.abs(t.amount)).replace("GH₵", "GHS")}
                  </div>
                </div>
              ))}
            </div>
          </section>
          )}

          {/* Compact dismissible Sponsored card moved up next to wallet (Round 100) */}
          <SupportWidget />
          <CampaignsStrip />
          <AdsRotator />
          <WhatsAppChannelCard />
        </div>
      </div>
    </div>
  );
}
